import React from 'react'
import { Button, Grid, MenuItem, Stack } from '@mui/material'
import { FormProvider, useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { useTranslation } from 'react-i18next'

import Modal from 'components/Modal'
import TextField from 'packages/ui/TextField'
import { User } from 'models/user.model'
import { CIVILITIES, COUNTRY } from 'pages/Contacts/Edit/constants'

import { getCountryLabel } from './constants'
import useUpdateProfile, { buildProfileSchema, ProfileFormValues } from './use-update-profile'

interface EditProfileModalProps {
  user: User
  onClose: () => void
}

const EditProfileModal: React.FC<EditProfileModalProps> = ({ user, onClose }) => {
  const { t, i18n } = useTranslation()
  const { internal } = user
  const methods = useForm<ProfileFormValues>({
    defaultValues: {
      first_name: user.first_name ?? '',
      last_name: user.last_name ?? '',
      title: user.title ?? '',
      country: user.country ?? 'fr',
      phone: user.phone ?? '',
      ...(internal ? {} : {
        salutation: user.salutation ?? 'M',
        mobile_phone: user.mobile_phone ?? ''
      })
    },
    resolver: yupResolver(buildProfileSchema({ t }))
  })
  const { updateProfile, loading } = useUpdateProfile({ onSuccess: onClose })

  return (
    <Modal
      icon={<i className="fa-light fa-pen" />}
      title="profile.edit.title"
      onClose={onClose}
      minWidth="560px"
    >
      <FormProvider {...methods}>
        <Grid container spacing={2}>
          {!internal && (
            <Grid item xs={4}>
              <TextField
                name="salutation"
                select
                label={t('auth.admin.edit.title_placeholder')}>
                {CIVILITIES.map(({ value }) => (
                  <MenuItem key={value} value={value} sx={{ fontSize: '12px' }}>
                    {t(`profile.edit.civility.${value}`)}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
          )}
          <Grid item xs={internal ? 6 : 4}>
            <TextField
              name="first_name"
              required
              label={t('auth.admin.edit.firstname_placeholder')}
            />
          </Grid>
          <Grid item xs={internal ? 6 : 4}>
            <TextField
              name="last_name"
              required
              label={t('auth.admin.edit.lastname_placeholder')}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              name="title"
              label={t('auth.admin.edit.position_placeholder')}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              name="country"
              select
              label={t('auth.admin.edit.country_placeholder')}>
              {COUNTRY.map(({ value }) => (
                <MenuItem key={value} value={value} sx={{ fontSize: '12px' }}>
                  {getCountryLabel(value, i18n.language)}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={6}>
            <TextField
              name="phone"
              type="tel"
              label={t('auth.admin.edit.phone_placeholder')}
            />
          </Grid>
          {!internal && (
            <Grid item xs={6}>
              <TextField
                name="mobile_phone"
                type="tel"
                label={t('auth.admin.edit.mobile_placeholder')}
              />
            </Grid>
          )}
        </Grid>
        <Stack direction="row" justifyContent="space-between">
          <Button
            size="small"
            variant="outlined"
            onClick={onClose}
            disabled={loading}>
            {t('ui_elements.cancel')}
          </Button>
          <Button
            size="small"
            variant="contained"
            color="primary"
            onClick={methods.handleSubmit(updateProfile)}
            disabled={loading}>
            {t('ui_elements.form_validate')}
          </Button>
        </Stack>
      </FormProvider>
    </Modal>
  )
}

export default EditProfileModal
